/**
 * useFounderKeys — list / mint / revoke RAELA & app admin API tokens.
 *
 * Plain token strings only exist briefly in `mintedToken` after a fresh
 * mint. Once the founder navigates away or calls `clearMintedToken`,
 * the plain value is gone forever (only the hash lives in the DB).
 */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

export interface FounderKeyRow {
  id: string;
  label: string;
  token_prefix: string;
  scopes: string[];
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export const useFounderKeys = () => {
  const { user } = useAuth();
  const [keys, setKeys] = useState<FounderKeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mintedToken, setMintedToken] = useState<{
    id: string;
    label: string;
    token: string;
  } | null>(null);

  const reload = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("founder_admin_keys")
      .select("id, label, token_prefix, scopes, created_at, last_used_at, revoked_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    if (error) setError(error.message);
    setKeys((data as FounderKeyRow[] | null) ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const mint = useCallback(
    async (label: string, master = false) => {
      setError(null);
      const { data, error } = await supabase.functions.invoke<{
        id: string;
        label: string;
        token: string;
        prefix: string;
        scopes: string[];
        created_at: string;
      }>("founder-mint-key", { body: { label, master } });
      if (error || !data) {
        setError(error?.message ?? "Failed to mint key");
        return null;
      }
      setMintedToken({ id: data.id, label: data.label, token: data.token });
      await reload();
      return data;
    },
    [reload],
  );

  const revoke = useCallback(
    async (id: string) => {
      const { error } = await supabase
        .from("founder_admin_keys")
        .update({ revoked_at: new Date().toISOString() })
        .eq("id", id);
      if (error) {
        setError(error.message);
        return false;
      }
      await reload();
      return true;
    },
    [reload],
  );

  const clearMintedToken = () => setMintedToken(null);

  return { keys, loading, error, mint, revoke, mintedToken, clearMintedToken };
};
